import { useCallback } from 'react';
import classNames from 'classnames';
import useControllableState from 'helpers/hooks/useControllableState';
import { QuantitySelectorProps } from './types';

const QuantitySelector = ({
  value: valueProp,
  defaultValue = 0,
  minValue = -Infinity,
  maxValue = Infinity,
  disabled = false,
  className: classNameProp,
  onChange,
}: QuantitySelectorProps) => {
  const normalizeValue = useCallback(
    (val: number) => Math.max(minValue, Math.min(maxValue, val)),
    [minValue, maxValue],
  );

  const [value, setValue] = useControllableState(valueProp ? normalizeValue(valueProp) : undefined, defaultValue);

  const className = classNames(
    'flex w-fit items-center gap-3 rounded-full border border-neutral-500 transition hover:border-neutral-800',
    {
      'cursor-not-allowed bg-neutral-200 text-gray-400': disabled,
      'cursor-pointer bg-white text-secondary-black': !disabled,
    },
    classNameProp,
  );

  const handleChange = useCallback(
    (val: number) => {
      if (disabled) return;

      const normalizedValue = normalizeValue(val);

      setValue(normalizedValue);
      onChange?.(normalizedValue);
    },
    [setValue, normalizeValue, onChange, disabled],
  );

  return (
    <div className={className}>
      <button onClick={() => handleChange(value - 1)} className="cursor-[inherit] py-[3px] pl-3">
        -
      </button>
      <span className="py-[3px] text-[14px]">{value}</span>
      <button onClick={() => handleChange(value + 1)} className="cursor-[inherit] py-[3px] pr-3">
        +
      </button>
    </div>
  );
};

export default QuantitySelector;
